import React from 'react';
import {
    Box, Typography, Button, Checkbox, FormControlLabel, Collapse,
    CircularProgress, MenuItem, TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CampaignProgress, { SessionNarrativeList } from '../../CampaignProgress';
import { EventBroker } from '../../../eventbroker';
import { campaignPanelSummary } from './campaignPanelState';
import { campaignActivityLine, connectionActivity } from './campaignActivity';
import { chimeKindForSummary, playCampaignChime } from './campaignChime';
import {
    CAMPAIGN_AUTONOMY_MODES,
    DEFAULT_CAMPAIGN_AUTONOMY_MODE,
} from '../../../gibbs/autonomousCampaign/workflowSubmission';

/**
 * Track what the active step is doing, so a long action reports progress instead
 * of an unchanging spinner.
 *
 * Progress ticks are deliberately kept out of `campaignPanel.events`: that array
 * is the campaign's audit trail, and a batched action would flood it with entries
 * that mean nothing after the fact. This is ephemeral view state only.
 *
 * The 1s tick exists because nothing arrives while a step waits on the server —
 * elapsed time and connection silence are precisely the signals with no event.
 */
function useCampaignActivity(running, getDataConnection) {
    const [progress, setProgress] = React.useState(null);
    const [nowMs, setNowMs] = React.useState(() => Date.now());
    const startedAtRef = React.useRef(null);

    React.useEffect(() => {
        if (!running) {
            startedAtRef.current = null;
            setProgress(null);
            return undefined;
        }
        startedAtRef.current = Date.now();
        setNowMs(Date.now());
        // EventBroker invokes handlers as handler(eventName, data).
        const onBatchProgress = (_eventName, payload) => {
            setProgress(payload && typeof payload === 'object' ? payload : null);
        };
        EventBroker.subscribe('batchProgress', onBatchProgress);
        const timer = setInterval(() => setNowMs(Date.now()), 1000);
        return () => {
            EventBroker.unsubscribe('batchProgress', onBatchProgress);
            clearInterval(timer);
        };
    }, [running]);

    if (!running) return { text: '', tone: 'info', disconnected: false };

    const startedAt = startedAtRef.current;
    const elapsedMs = Number.isFinite(startedAt) ? nowMs - startedAt : null;
    const { connected } = connectionActivity(
        typeof getDataConnection === 'function' ? getDataConnection() : null,
    );
    return campaignActivityLine({ progress, elapsedMs, connected });
}

const AUTONOMY_OPTIONS = [
    {
        value: CAMPAIGN_AUTONOMY_MODES.GUIDED,
        label: 'Guided — review every step',
        description: 'Pause after each action for an explicit Continue or Stop decision.',
    },
    {
        value: CAMPAIGN_AUTONOMY_MODES.REVIEW_BRANCHES,
        label: 'Review branches and retries',
        description: 'Proceed through the reviewed spine, but pause before branches, retries, or escalations.',
    },
    {
        value: CAMPAIGN_AUTONOMY_MODES.FULL_AUTO,
        label: 'Autonomous within budget',
        description: 'Run the reviewed workflow without routine pauses, subject to its signed safety budget.',
    },
];

const toneColors = {
    default: '#9e9e9e',
    info: '#2196f3',
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
};

// How many events the streamed region renders at once. The cap is what keeps the
// step list below it on screen; it is a rendering window and nothing else.
const EVENT_WINDOW = 6;

function latestIndex(events, predicate) {
    for (let i = events.length - 1; i >= 0; i -= 1) {
        if (predicate(events[i])) return i;
    }
    return -1;
}

function activeExpansionSignal(events, running, escalation) {
    if (escalation) {
        return `checkpoint:${escalation.escalationId || escalation.id || 'active'}`;
    }
    if (running) {
        const terminalIndex = latestIndex(events, (event) => event?.type === 'campaign-complete');
        return `running-after:${terminalIndex}`;
    }
    const terminalIndex = latestIndex(events, (event) => event?.type === 'campaign-complete');
    if (terminalIndex >= 0) {
        const event = events[terminalIndex] || {};
        return `terminal:${terminalIndex}:${event.status || 'complete'}`;
    }
    return null;
}

function EventStatusLine({ event, formatText }) {
    const message = event.message || `Campaign ${event.status}`;
    return (
        <Typography variant="body2" color="text.secondary">
            {formatText(message)}
        </Typography>
    );
}

function VectorDetails({
    selectedAtomCount,
    selectedGrowVector,
    vectorDisplay,
    vectorReason,
    hasSelectedBondVector,
    growVectorReversed,
    onGrowVectorReverseChange,
    formatCompoundId,
}) {
    return (
        <>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1 }}
            >
                {selectedAtomCount}
                /2 atoms selected
            </Typography>
            {vectorDisplay && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                    data-testid="workflow-campaign-selected-vector"
                >
                    Selected vector:
                    {' '}
                    {vectorDisplay}
                    {selectedGrowVector.parentId ? ` (${formatCompoundId(selectedGrowVector.parentId)})` : ''}
                </Typography>
            )}
            {!hasSelectedBondVector && vectorReason && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mb: 1 }}
                >
                    Invalid vector:
                    {' '}
                    {vectorReason}
                </Typography>
            )}
            {hasSelectedBondVector && (
                <Button
                    size="small"
                    variant="outlined"
                    sx={{ mb: 1 }}
                    onClick={() => onGrowVectorReverseChange?.(!growVectorReversed)}
                    data-testid="workflow-campaign-swap-vector"
                >
                    Swap Direction
                </Button>
            )}
        </>
    );
}

const DOCKING_SITE_LOCKED_HELP = 'The structure this run was signed against. To dock '
    + 'into a different one, propose the workflow again and choose before starting.';

export default function WorkflowCampaignPanel({
    campaignPanel,
    onEscalationDecision,
    campaignAutonomyMode = DEFAULT_CAMPAIGN_AUTONOMY_MODE,
    onCampaignAutonomyModeChange,
    configurationEnabled = true,
    campaignBudgetDerivation = null,
    referenceRoleSummary = null,
    campaignArtifacts = [],
    onDiscardNarratives,
    // Returns a text formatter bound to one document's own alias bindings. A
    // single shared formatter decodes with whatever the workspace looks like now,
    // which is wrong for a document written against an earlier one — or restored
    // from a file.
    artifactMarkdownFormatter = null,
    // Set while the workspace a checkpoint would be answered against is not
    // there — during a reconnect, or after one that did not restore it. The
    // controls come off rather than going grey: every reply here carries live
    // atom selections and grow vectors, so there is nothing partial to offer.
    checkpointBlockedReason = null,
    unresolvedEarlierCampaign = null,
    onDiscardEarlierCampaign,
    dockingSite = null,
    dockingSiteLocked = false,
    onDockingSiteChange,
    growCheckpointRequired = false,
    selectedAtomCount = 0,
    selectedGrowVector = null,
    growVectorReversed = false,
    onGrowVectorReverseChange,
    formatCompoundId = (maskedId) => maskedId,
    formatText = (text) => text,
    formatMarkdown = (text) => text,
    // Optional: lets the panel report connection silence during a long step.
    // Absent, activity still reports progress and elapsed time.
    getDataConnection = null,
}) {
    const [applySharedVectorToAll, setApplySharedVectorToAll] = React.useState(false);
    const [parentVectorResolutions, setParentVectorResolutions] = React.useState({});
    const events = campaignPanel?.events || [];
    const escalation = campaignPanel?.escalation || null;
    const running = campaignPanel?.running === true;
    const summary = campaignPanelSummary(campaignPanel);
    const activity = useCampaignActivity(running, getDataConnection);
    const runHasStarted = events.some((event) => (
        event?.type === 'decision-record'
        || event?.type === 'campaign-complete'
        || (event?.type === 'campaign-status'
            && ['requested', 'running', 'restoring', 'resumed', 'cancelling'].includes(event.status))
    ));
    // Whether the site is still a control comes from the plan lifecycle, passed in
    // — NOT from `runHasStarted`. Terminal events survive a reload of an existing
    // Deputy session, so keying off them locked a newly proposed workflow out of
    // the very selector its refusal message points at.
    const siteLocked = running || dockingSiteLocked;
    // Off by default. The per-step reasoning is a paragraph each and it is what
    // the narrative is for; shown inline for every step it pushes the step list
    // itself out of view.
    const [showRationale, setShowRationale] = React.useState(false);
    const expansionSignal = activeExpansionSignal(events, running, escalation)
        || (configurationEnabled && !runHasStarted ? 'configuration' : null);
    const [expanded, setExpanded] = React.useState(Boolean(expansionSignal));
    const [budgetBreakdownOpen, setBudgetBreakdownOpen] = React.useState(false);
    const previousExpansionSignal = React.useRef(expansionSignal);
    const contentId = React.useId();
    const toggleId = React.useId();
    const chimedTerminalKey = React.useRef(null);

    const chimeKind = chimeKindForSummary(summary);
    const terminalEventCount = events.filter((event) => event?.type === 'campaign-complete').length;

    // Sound the terminal chime once per ending, on the transition into it. Keyed
    // by kind plus the terminal event count so a re-render, a reopened panel, or
    // a restored session cannot replay it, while a genuine second run can.
    React.useEffect(() => {
        if (!chimeKind) {
            chimedTerminalKey.current = null;
            return;
        }
        const key = `${chimeKind}:${terminalEventCount}`;
        if (chimedTerminalKey.current === key) return;
        chimedTerminalKey.current = key;
        playCampaignChime(chimeKind);
    }, [chimeKind, terminalEventCount]);

    React.useEffect(() => {
        if (expansionSignal && expansionSignal !== previousExpansionSignal.current) {
            setExpanded(true);
        }
        previousExpansionSignal.current = expansionSignal;
    }, [expansionSignal]);

    React.useEffect(() => {
        setApplySharedVectorToAll(false);
        setParentVectorResolutions({});
    }, [expansionSignal]);

    const requestedInput = escalation?.prompt?.requestedInput || escalation?.requestedInput;
    const isActionRecovery = escalation?.prompt?.kind === 'action-recovery';
    const requiresSelectedBondVector = requestedInput === 'selected-bond-vector';
    const approvesSuggestedBondVector = requestedInput === 'suggested-bond-vector';
    const requiresMultiParentVectors = requestedInput === 'multi-parent-bond-vectors';
    const recommendedDecision = escalation?.prompt?.decision;
    const recommendedAction = typeof recommendedDecision?.action === 'string'
        ? recommendedDecision.action
        : null;
    const recommendedNextActionId = typeof recommendedDecision?.nextActionId === 'string'
        ? recommendedDecision.nextActionId
        : null;
    const reviewsRecommendedBranch = !requestedInput
        && !isActionRecovery
        && recommendedAction === 'branch'
        && Boolean(recommendedNextActionId);
    const reviewsRecommendedRetry = !requestedInput
        && !isActionRecovery
        && recommendedAction === 'retry';
    const eligibleParentIds = Array.isArray(escalation?.prompt?.eligibleParentIds)
        ? escalation.prompt.eligibleParentIds.filter((id) => typeof id === 'string')
        : [];
    let targetParentId = null;
    if (typeof escalation?.prompt?.targetParentId === 'string'
        && escalation.prompt.targetParentId.length > 0) {
        targetParentId = escalation.prompt.targetParentId;
    } else if (eligibleParentIds.length === 1) {
        [targetParentId] = eligibleParentIds;
    }
    let remainingParentIds = targetParentId ? [targetParentId] : [];
    if (Array.isArray(escalation?.prompt?.remainingParentIds)) {
        remainingParentIds = escalation.prompt.remainingParentIds
            .filter((id) => typeof id === 'string');
    }
    const hasSelectedBondVector = selectedGrowVector?.valid === true;
    const selectedVectorMatchesTargetParent = !targetParentId
        || selectedGrowVector?.parentId === targetParentId;
    const parentResolutionList = eligibleParentIds.map((parentId) => (
        parentVectorResolutions[parentId] || { parentId, status: 'pending' }
    ));
    const unresolvedParents = requiresMultiParentVectors
        ? parentResolutionList.filter((entry) => !['applied', 'skipped'].includes(entry.status))
        : [];
    const continueDisabled = (requiresSelectedBondVector && (
        !hasSelectedBondVector || !selectedVectorMatchesTargetParent
    ))
        || (requiresMultiParentVectors && unresolvedParents.length > 0);
    const vectorReason = selectedGrowVector?.reason || null;
    const vectorDisplay = selectedGrowVector?.valid && selectedGrowVector.display
        ? `${selectedGrowVector.display.from} -> ${selectedGrowVector.display.to}`
        : null;
    const selectedAutonomy = AUTONOMY_OPTIONS.find(
        (option) => option.value === campaignAutonomyMode,
    ) || AUTONOMY_OPTIONS[0];
    const indexedEvents = events.map((event, index) => ({ event, index }));
    const isDecisionRecord = (entry) => entry.event?.type === 'decision-record';
    // Whether any reasoning exists is a question about the whole run, not about the
    // rendering window. On a failed run the terminal frames — cancelling, failed,
    // campaign-complete, status lines — pile up after the last decision record and
    // can push every record out of the window while the records are still held.
    const hasStepReasoning = indexedEvents.some(isDecisionRecord);
    const recentEvents = indexedEvents.slice(-EVENT_WINDOW).reverse();
    // ...and once the toggle is offered it has to deliver. When the window holds no
    // record, the most recent one takes the oldest slot, so the stream stays capped
    // at EVENT_WINDOW and the step list below is never pushed off screen.
    const rationaleEvents = hasStepReasoning && !recentEvents.some(isDecisionRecord)
        ? [
            ...recentEvents.slice(0, EVENT_WINDOW - 1),
            indexedEvents.filter(isDecisionRecord).slice(-1)[0],
        ]
        : recentEvents;
    // The decision stream is hidden outright, not just its rationale. The step
    // list below this region already reports what ran and how it ended; a second
    // rendering of the same steps, in reverse, was the thing pushing that list off
    // screen. One toggle brings the whole stream back.
    const visibleEvents = showRationale
        ? rationaleEvents
        : recentEvents.filter((entry) => !isDecisionRecord(entry));
    const toneColor = toneColors[summary.tone] || toneColors.default;
    const applyVectorToParent = (parentId, source = 'parent-specific-user') => {
        if (!hasSelectedBondVector) return;
        const atomUniqueIds = Array.isArray(selectedGrowVector.atomUniqueIds)
            ? selectedGrowVector.atomUniqueIds.slice(0, 2)
            : [];
        if (atomUniqueIds.length !== 2) return;
        setParentVectorResolutions((prev) => ({
            ...prev,
            [parentId]: {
                parentId,
                status: 'applied',
                source,
                atomUniqueIds,
            },
        }));
    };
    const skipParent = (parentId) => {
        setParentVectorResolutions((prev) => ({
            ...prev,
            [parentId]: {
                parentId,
                status: 'skipped',
                source: 'user-skipped',
            },
        }));
    };
    const skipAllRemaining = () => {
        setParentVectorResolutions((prev) => {
            const next = { ...prev };
            eligibleParentIds.forEach((parentId) => {
                if (next[parentId]?.status === 'applied') return;
                next[parentId] = {
                    parentId,
                    status: 'skipped',
                    source: 'user-skipped',
                };
            });
            return next;
        });
    };
    const applySharedVector = () => {
        if (!hasSelectedBondVector) return;
        const atomUniqueIds = Array.isArray(selectedGrowVector.atomUniqueIds)
            ? selectedGrowVector.atomUniqueIds.slice(0, 2)
            : [];
        if (atomUniqueIds.length !== 2) return;
        setApplySharedVectorToAll(true);
        setParentVectorResolutions((prev) => {
            const next = { ...prev };
            eligibleParentIds.forEach((parentId) => {
                if (next[parentId]?.status === 'skipped') return;
                next[parentId] = {
                    parentId,
                    status: 'applied',
                    source: 'shared-reused',
                    atomUniqueIds,
                };
            });
            return next;
        });
    };
    const sequentialAppliedResolution = requiresSelectedBondVector
        && targetParentId
        && hasSelectedBondVector
        && selectedVectorMatchesTargetParent
        ? [{
            parentId: targetParentId,
            status: 'applied',
            source: 'parent-specific-user',
            atomUniqueIds: selectedGrowVector.atomUniqueIds.slice(0, 2),
        }]
        : null;
    let continueOptions = {};
    if (requiresMultiParentVectors) {
        continueOptions = { parentVectorResolutions: parentResolutionList };
    } else if (sequentialAppliedResolution) {
        continueOptions = { parentVectorResolutions: sequentialAppliedResolution };
    }
    const sequentialSkipOptions = (parentIds) => ({
        parentVectorResolutions: parentIds.map((parentId) => ({
            parentId,
            source: 'user-skipped',
            status: 'skipped',
        })),
    });

    let checkpointMessage = 'Campaign checkpoint requires confirmation.';
    if (isActionRecovery) {
        checkpointMessage = escalation?.prompt?.reason
            || 'The previous live action has no durable result. Retry may repeat computation.';
    } else if (requiresSelectedBondVector) {
        checkpointMessage = targetParentId
            ? `Select two atoms on ${formatCompoundId(targetParentId)}: first the source atom, then the atom that defines the grow direction.`
            : 'Select two atoms on the target parent: first the source atom, then the atom that defines the grow direction.';
    } else if (requiresMultiParentVectors) {
        checkpointMessage = 'Select two atoms, then explicitly choose whether to reuse that vector across expected parents.';
    } else if (approvesSuggestedBondVector) {
        checkpointMessage = 'Approve the Deputy-suggested grow bond vector before continuing.';
    } else if (reviewsRecommendedBranch) {
        checkpointMessage = `Recommended branch: ${recommendedNextActionId}. ${
            recommendedDecision?.rationale || escalation?.prompt?.reason || ''
        }`.trim();
    } else if (reviewsRecommendedRetry) {
        checkpointMessage = `Recommended retry. ${
            recommendedDecision?.rationale || escalation?.prompt?.reason || ''
        }`.trim();
    } else if (escalation?.prompt?.reason) {
        checkpointMessage = escalation.prompt.reason;
    }
    // The engine writes these prompts at a masked boundary, so any compound it
    // names arrives as COMPOUND_x. The panel is the one surface that shows the
    // owner their own molecules, and it already decodes the decision stream and
    // the structured parent ids — the prompt text was the one place still
    // rendering the mask. Idempotent for text that carries no masked id, so it
    // covers the hand-written branches and any prompt added later.
    const decodedCheckpointMessage = formatText(checkpointMessage);
    let continueButtonLabel = 'Continue';
    if (reviewsRecommendedBranch || reviewsRecommendedRetry) {
        continueButtonLabel = 'Continue Planned Workflow';
    } else if (isActionRecovery) {
        continueButtonLabel = 'Retry Action';
    }

    return (
        <Box
            sx={{
                mt: 1,
                mb: 1,
                border: '1px solid',
                borderColor: escalation ? '#f0d98b' : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor: '#fff',
            }}
            data-testid="workflow-campaign-panel"
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: escalation ? '#fff8e1' : 'transparent',
            }}
            >
                <Button
                    id={toggleId}
                    type="button"
                    variant="text"
                    onClick={() => setExpanded((prev) => !prev)}
                    aria-expanded={expanded}
                    aria-controls={contentId}
                    data-testid="workflow-campaign-toggle"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        p: 0,
                        color: 'text.primary',
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'transparent' },
                    }}
                >
                    {expanded
                        ? <ExpandLessIcon fontSize="small" sx={{ mr: 0.5 }} />
                        : <ExpandMoreIcon fontSize="small" sx={{ mr: 0.5 }} />}
                    {running && !escalation ? (
                        <CircularProgress
                            size={14}
                            thickness={4}
                            sx={{ mr: 1, color: toneColor, flexShrink: 0 }}
                        />
                    ) : (
                        <Box
                            component="span"
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: toneColor,
                                mr: 1,
                                flexShrink: 0,
                                display: 'inline-block',
                            }}
                        />
                    )}
                    <Box
                        component="span"
                        sx={{
                            minWidth: 0,
                            textAlign: 'left',
                            flex: 1,
                            display: 'block',
                        }}
                    >
                        <Typography
                            component="span"
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{ display: 'block', lineHeight: 1.2 }}
                        >
                            Autonomy Options
                        </Typography>
                        <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: 'block',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {summary.label}
                            {' · '}
                            {formatText(summary.message)}
                        </Typography>
                        {/* Its own line, so a volatile counter never competes with
                            the status message for the ellipsis. Absent unless a
                            running step actually has something to report. */}
                        {activity.text ? (
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: activity.disconnected ? 'warning.main' : 'text.secondary',
                                }}
                            >
                                {activity.text}
                            </Typography>
                        ) : null}
                    </Box>
                </Button>
            </Box>
            <Collapse in={expanded}>
                <Box
                    id={contentId}
                    role="region"
                    aria-labelledby={toggleId}
                    aria-hidden={!expanded}
                    hidden={!expanded}
                    sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}
                >
                    {configurationEnabled && (
                        <>
                            <Box
                                sx={{
                                    p: 1, mb: 1, bgcolor: '#f8fbfa', borderRadius: 1,
                                }}
                                data-testid="workflow-campaign-configuration"
                            >
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.75 }}>
                                    Execution review
                                </Typography>
                                {/*
                                  * Neutral disclosure, not a warning. An unmarked
                                  * crystal ligand is the reviewed, signed setup in
                                  * workspaces A and B, so flagging it as a problem
                                  * would raise an alarm on most of the benchmark
                                  * set and advise a change that breaks them. State
                                  * what the run will treat as what, and let a
                                  * reader who knows the chemistry draw the
                                  * conclusion. Nothing here reaches the signed
                                  * submission or the narrative.
                                  */}
                                {referenceRoleSummary && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1 }}
                                        data-testid="workflow-campaign-reference-roles"
                                    >
                                        {referenceRoleSummary}
                                    </Typography>
                                )}
                                {/*
                                  * Which structure this campaign docks into.
                                  * Shown only when the workspace holds more than
                                  * one, because with a single structure there is
                                  * no decision and offering one would misrepresent
                                  * what the reviewer controls.
                                  *
                                  * "Both, to compare selectivity" is listed and
                                  * disabled. The request is legitimate — it is what
                                  * a selectivity study is — but nothing after the
                                  * dock step is attributed per structure, so a run
                                  * would rank two proteins' interaction energies in
                                  * one column and let the larger molecule win. A
                                  * visible option with its reason attached says
                                  * what is coming; a working one would print a
                                  * confident, meaningless ranking.
                                  */}
                                {dockingSite?.applicable && (
                                    <>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Docking site"
                                            value={dockingSite.value || ''}
                                            error={dockingSite.needsChoice && !siteLocked}
                                            disabled={siteLocked}
                                            onChange={(event) => {
                                                // Guarded as well as disabled. Changing this
                                                // writes the workspace's target role, so the
                                                // invariant belongs in code rather than resting
                                                // on the widget's disabled state.
                                                if (siteLocked) return;
                                                onDockingSiteChange?.(event.target.value);
                                            }}
                                            sx={{ mb: 1 }}
                                            slotProps={{
                                                htmlInput: {
                                                    'data-testid': 'workflow-campaign-docking-site-select',
                                                },
                                            }}
                                        >
                                            {dockingSite.options.map((option) => (
                                                <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                    disabled={option.disabled}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <Typography
                                            variant="caption"
                                            color={dockingSite.needsChoice && !siteLocked
                                                ? 'error'
                                                : 'text.secondary'}
                                            sx={{ display: 'block', mt: -0.5, mb: 1 }}
                                            data-testid="workflow-campaign-docking-site-help"
                                        >
                                            {siteLocked
                                                ? DOCKING_SITE_LOCKED_HELP
                                                : dockingSite.helperText}
                                        </Typography>
                                        {/*
                                          * Why an option is unavailable is guidance for making a
                                          * choice. Once the run is signed there is no choice left,
                                          * so it would just be noise on a finished panel.
                                          */}
                                        {!siteLocked && dockingSite.options
                                            .filter((option) => option.disabled && option.reason)
                                            .map((option) => (
                                                <Typography
                                                    key={option.value}
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}
                                                    data-testid="workflow-campaign-docking-site-unavailable"
                                                >
                                                    {`${option.label}: ${option.reason}`}
                                                </Typography>
                                            ))}
                                    </>
                                )}
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Autonomy level"
                                    value={campaignAutonomyMode}
                                    disabled={running}
                                    onChange={(event) => (
                                        onCampaignAutonomyModeChange?.(event.target.value)
                                    )}
                                    slotProps={{
                                        htmlInput: {
                                            'data-testid': 'workflow-campaign-autonomy-select',
                                        },
                                    }}
                                >
                                    {AUTONOMY_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}
                                    data-testid="workflow-campaign-autonomy-description"
                                >
                                    {selectedAutonomy.description}
                                </Typography>
                                {campaignBudgetDerivation && (
                                    <Box
                                        sx={{
                                            mt: 1,
                                            p: 0.75,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                        data-testid="workflow-campaign-step-budget"
                                    >
                                        {/* The signed arithmetic stays on one line; the labelled
                                            per-block sources are one click away so a long plan
                                            does not push the launch controls off screen. */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: 0.5,
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setBudgetBreakdownOpen((open) => !open)}
                                            data-testid="workflow-campaign-step-budget-toggle"
                                        >
                                            <Typography variant="caption" fontWeight="bold">
                                                Action-dispatch cap:
                                                {' '}
                                                {campaignBudgetDerivation.total}
                                                {' = '}
                                                {campaignBudgetDerivation.reserve}
                                                {' reserve'}
                                                {campaignBudgetDerivation.blocks.map((block) => (
                                                    <React.Fragment key={block.id}>
                                                        {' + '}
                                                        {block.stepsPerMolecule}
                                                        ×
                                                        {block.moleculeCount}
                                                    </React.Fragment>
                                                ))}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {budgetBreakdownOpen ? '▾' : '▸'}
                                            </Typography>
                                        </Box>
                                        <Collapse in={budgetBreakdownOpen}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mt: 0.25 }}
                                            >
                                                Bounds:
                                                {' '}
                                                {campaignBudgetDerivation.upperBounds
                                                    .defaultDockPosesPerMolecule}
                                                {' dock poses when unreviewed · '}
                                                {campaignBudgetDerivation.upperBounds
                                                    .growProductsPerAction}
                                                {' grow products per action'}
                                            </Typography>
                                            {campaignBudgetDerivation.blocks.map((block) => (
                                                <Typography
                                                    key={block.id}
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', mt: 0.25 }}
                                                >
                                                    {block.id}
                                                    {': '}
                                                    {block.stepsPerMolecule}
                                                    {' steps × '}
                                                    {block.moleculeCount}
                                                    {' molecules = '}
                                                    {block.subtotal}
                                                    {' · '}
                                                    {block.moleculeCountSource}
                                                </Typography>
                                            ))}
                                        </Collapse>
                                    </Box>
                                )}
                                {growCheckpointRequired && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 0.5 }}
                                        data-testid="workflow-campaign-runtime-grow-guidance"
                                    >
                                        When execution reaches growth, the campaign will identify
                                        each target parent before asking you to select two atoms.
                                    </Typography>
                                )}
                            </Box>
                        </>
                    )}
                    {escalation && (
                        <Box
                            sx={{
                                p: 1,
                                mb: 1,
                                backgroundColor: '#fff8e1',
                                border: '1px solid',
                                borderColor: '#f0d98b',
                                borderRadius: 1,
                            }}
                            data-testid="workflow-campaign-escalation"
                        >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {decodedCheckpointMessage}
                            </Typography>
                            {checkpointBlockedReason && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
                                    data-testid="workflow-campaign-checkpoint-blocked"
                                >
                                    {checkpointBlockedReason}
                                </Typography>
                            )}
                            {!checkpointBlockedReason && approvesSuggestedBondVector && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 1 }}
                                >
                                    The Deputy will use a bounded live BFD candidate vector
                                    instead of manual atom selection.
                                </Typography>
                            )}
                            {!checkpointBlockedReason && requiresMultiParentVectors && (
                                <>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1 }}
                                    >
                                        {eligibleParentIds.length}
                                        {' '}
                                        expected parent
                                        {eligibleParentIds.length === 1 ? '' : 's'}
                                        {eligibleParentIds.length
                                            ? `: ${eligibleParentIds.map(formatCompoundId).join(', ')}`
                                            : ''}
                                    </Typography>
                                    <VectorDetails
                                        selectedAtomCount={selectedAtomCount}
                                        selectedGrowVector={selectedGrowVector}
                                        vectorDisplay={vectorDisplay}
                                        vectorReason={vectorReason}
                                        hasSelectedBondVector={hasSelectedBondVector}
                                        growVectorReversed={growVectorReversed}
                                        onGrowVectorReverseChange={onGrowVectorReverseChange}
                                        formatCompoundId={formatCompoundId}
                                    />
                                    <Box sx={{
                                        display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap',
                                    }}
                                    >
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={applySharedVector}
                                            disabled={!hasSelectedBondVector}
                                            data-testid="workflow-campaign-apply-shared-vector"
                                        >
                                            Reuse Vector
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={skipAllRemaining}
                                            data-testid="workflow-campaign-skip-all-parents"
                                        >
                                            Skip All Remaining
                                        </Button>
                                    </Box>
                                    <Box sx={{ display: 'grid', gap: 0.5, mb: 1 }}>
                                        {parentResolutionList.map((entry) => {
                                            const selectedParent = selectedGrowVector?.parentId
                                                || null;
                                            const canApplySpecific = hasSelectedBondVector
                                                && (!selectedParent
                                                    || selectedParent === entry.parentId);
                                            return (
                                                <Box
                                                    key={entry.parentId}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        flexWrap: 'wrap',
                                                    }}
                                                    data-testid={`workflow-campaign-parent-${entry.parentId}`}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ minWidth: 92, fontWeight: 'bold' }}
                                                    >
                                                        {formatCompoundId(entry.parentId)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {entry.status}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => (
                                                            applyVectorToParent(entry.parentId)
                                                        )}
                                                        disabled={!canApplySpecific}
                                                        data-testid={`workflow-campaign-apply-parent-${entry.parentId}`}
                                                    >
                                                        Apply
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() => skipParent(entry.parentId)}
                                                        data-testid={`workflow-campaign-skip-parent-${entry.parentId}`}
                                                    >
                                                        Skip
                                                    </Button>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <FormControlLabel
                                        sx={{
                                            m: 0,
                                            mb: 1,
                                            '.MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                                        }}
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={applySharedVectorToAll}
                                                onChange={(event) => {
                                                    if (event.target.checked) {
                                                        applySharedVector();
                                                    } else {
                                                        setApplySharedVectorToAll(false);
                                                    }
                                                }}
                                                slotProps={{
                                                    input: { 'data-testid': 'workflow-campaign-shared-vector-toggle' },
                                                }}
                                            />
                                        )}
                                        label="Apply selected vector to all applicable parents"
                                    />
                                </>
                            )}
                            {!checkpointBlockedReason && requiresSelectedBondVector && (
                                <>
                                    {targetParentId && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 1 }}
                                            data-testid="workflow-campaign-target-parent"
                                        >
                                            Target parent:
                                            {' '}
                                            {formatCompoundId(targetParentId)}
                                        </Typography>
                                    )}
                                    <VectorDetails
                                        selectedAtomCount={selectedAtomCount}
                                        selectedGrowVector={selectedGrowVector}
                                        vectorDisplay={vectorDisplay}
                                        vectorReason={vectorReason}
                                        hasSelectedBondVector={
                                            hasSelectedBondVector
                                            && selectedVectorMatchesTargetParent
                                        }
                                        growVectorReversed={growVectorReversed}
                                        onGrowVectorReverseChange={onGrowVectorReverseChange}
                                        formatCompoundId={formatCompoundId}
                                    />
                                    {hasSelectedBondVector
                                        && !selectedVectorMatchesTargetParent && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            sx={{ display: 'block', mb: 1 }}
                                            data-testid="workflow-campaign-wrong-parent-vector"
                                        >
                                            Selected vector parent
                                            {' '}
                                            {selectedGrowVector?.parentId
                                                ? formatCompoundId(selectedGrowVector.parentId)
                                                : '(unknown)'}
                                            {' '}
                                            does not match target parent
                                            {' '}
                                            {formatCompoundId(targetParentId)}
                                        </Typography>
                                    )}
                                    {targetParentId && (
                                        <Box
                                            sx={{
                                                display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap',
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => onEscalationDecision(
                                                    'proceed', sequentialSkipOptions([targetParentId])
                                                )}
                                                data-testid="workflow-campaign-skip-sequential-parent"
                                            >
                                                Skip This Parent
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => onEscalationDecision(
                                                    'proceed', sequentialSkipOptions(remainingParentIds)
                                                )}
                                                data-testid="workflow-campaign-skip-sequential-remaining"
                                            >
                                                Skip All Remaining
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            )}
                            {!checkpointBlockedReason && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {reviewsRecommendedBranch && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => onEscalationDecision('branch', {
                                            nextActionId: recommendedNextActionId,
                                        })}
                                        data-testid="workflow-campaign-accept-branch"
                                    >
                                        Take Recommended Branch
                                    </Button>
                                )}
                                {reviewsRecommendedRetry && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => onEscalationDecision('retry')}
                                        data-testid="workflow-campaign-accept-retry"
                                    >
                                        Retry Action
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    variant={reviewsRecommendedBranch || reviewsRecommendedRetry
                                        ? 'outlined'
                                        : 'contained'}
                                    onClick={() => onEscalationDecision('proceed', continueOptions)}
                                    disabled={continueDisabled}
                                    data-testid="workflow-campaign-continue-spine"
                                >
                                    {continueButtonLabel}
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => onEscalationDecision('terminate')}
                                >
                                    Stop
                                </Button>
                            </Box>
                            )}
                        </Box>
                    )}
                    {hasStepReasoning && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setShowRationale((prev) => !prev)}
                            sx={{ alignSelf: 'flex-start', mb: 0.5, px: 0.5 }}
                            data-testid="workflow-campaign-rationale-toggle"
                        >
                            {showRationale ? 'Hide step reasoning' : 'Show step reasoning'}
                        </Button>
                    )}
                    {recentEvents.length ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 0.75,
                            }}
                            data-testid="workflow-campaign-events"
                        >
                            {visibleEvents.map(({ event, index }) => (
                                event.type === 'campaign-status' ? (
                                    <EventStatusLine
                                        key={`status-${index}`}
                                        event={event}
                                        formatText={formatText}
                                    />
                                ) : (
                                    <CampaignProgress
                                        key={`campaign-${index}`}
                                        event={event}
                                        formatText={formatText}
                                        formatMarkdown={formatMarkdown}
                                        showRationale={showRationale}
                                    />
                                )
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Review the execution policy above, then use the single
                            Workflow Checker start action when this workflow is ready.
                        </Typography>
                    )}
                    {/*
                      * An earlier campaign the Orchestrator still holds. Stated as
                      * its own thing rather than merged into this proposal's
                      * controls: it is not what the user is looking at, and its
                      * Retry belonged to a run they may not even remember starting.
                      */}
                    {unresolvedEarlierCampaign && (
                        <Box
                            sx={{
                                mt: 1,
                                p: 0.75,
                                border: '1px solid',
                                borderColor: 'warning.light',
                                borderRadius: 1,
                            }}
                            data-testid="workflow-campaign-earlier-unresolved"
                        >
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                                An earlier campaign in this session never finished
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                            >
                                It is not part of the workflow above and will not run with
                                it. Starting this workflow leaves it behind.
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => onDiscardEarlierCampaign?.()}
                                data-testid="workflow-campaign-earlier-dismiss"
                            >
                                Dismiss
                            </Button>
                        </Box>
                    )}
                    <SessionNarrativeList
                        artifacts={campaignArtifacts}
                        formatMarkdown={formatMarkdown}
                        markdownFormatterFor={artifactMarkdownFormatter}
                        onDiscard={onDiscardNarratives}
                    />
                </Box>
            </Collapse>
        </Box>
    );
}
